<script lang="ts">
  import { t } from '$core/i18n';
  import {
    buildRecallGroups,
    primaryLabel,
    secondaryLabel,
    type SessionStore,
    type RecallGroup
  } from '$features/session';
  import type { ForecastDay } from '$features/forecast';
  import { Button } from '$ui/primitives/Button';
  import { Badge } from '$ui/primitives/Badge';
  import { EmptyState } from '$ui/primitives/EmptyState';
  import { Skeleton } from '$ui/primitives/Skeleton';

  let { store }: { store: SessionStore } = $props();

  let dayGroups: RecallGroup[] = $state([]);
  let loading = $state(false);

  $effect(() => {
    void store.loadForecast();
  });

  async function pickDay(day: ForecastDay) {
    await store.selectForecastDate(day.date);
    loading = true;
    try {
      dayGroups = await buildRecallGroups(store.forecastItemsForDate);
    } finally {
      loading = false;
    }
  }

  async function reviewSelected() {
    if (!store.selectedForecastDate) return;
    await store.enterModeBForDate(store.selectedForecastDate);
  }

  async function reviewToday() {
    await store.enterModeBForDueToday();
  }

  function dayNumber(day: ForecastDay): string {
    const d = new Date(day.timestamp);
    return String(d.getDate()).padStart(2, '0');
  }

  function monthShort(day: ForecastDay): string {
    const d = new Date(day.timestamp);
    return d
      .toLocaleDateString(undefined, { month: 'short' })
      .replace('.', '')
      .slice(0, 3);
  }

  function fmt(value: number, digits = 2): string {
    return value.toFixed(digits);
  }
</script>

<section class="view">
  <header class="view-head">
    <div>
      <h1 class="view-title">{t('forecast.title')}</h1>
      <p class="subtitle">{t('forecast.subtitle')}</p>
    </div>
  </header>

  <div class="stats">
    <div class="stat">
      <span class="stat-label">{t('forecast.dueToday')}</span>
      <span class="stat-value">{store.forecastStats.dueToday}</span>
    </div>
    <div class="stat">
      <span class="stat-label">{t('forecast.active')}</span>
      <span class="stat-value">{store.forecastStats.active}</span>
    </div>
    <div class="stat">
      <span class="stat-label">{t('forecast.avgStability')}</span>
      <span class="stat-value">{fmt(store.forecastStats.avgStability, 1)}</span>
    </div>
    <div class="stat">
      <span class="stat-label">{t('forecast.avgDifficulty')}</span>
      <span class="stat-value">{fmt(store.forecastStats.avgDifficulty, 1)}</span>
    </div>
  </div>

  {#if store.forecastStats.active === 0}
    <EmptyState
      icon="calendar-days"
      title={t('forecast.empty')}
      description={t('forecast.emptyHint')}
    />
  {:else}
    <p class="hint">{t('forecast.selectHint')}</p>

    <div class="heatmap" role="grid" aria-label={t('forecast.title')}>
      {#each store.forecastDays as day (day.date)}
        <button
          type="button"
          role="gridcell"
          class="cell"
          data-today={day.isToday || undefined}
          data-selected={store.selectedForecastDate === day.date || undefined}
          data-level={day.heatLevel}
          onclick={() => void pickDay(day)}
          title={`${day.date} · ${day.count}`}
          aria-label={t('forecast.dayAria', {
            date: day.date,
            count: day.count
          })}
        >
          <span class="cell-day">{dayNumber(day)}</span>
          <span class="cell-month">{monthShort(day)}</span>
          {#if day.count > 0}
            <span class="cell-count">{day.count}</span>
          {/if}
        </button>
      {/each}
    </div>

    <div class="selected">
      {#if store.selectedForecastDate}
        <div class="selected-header">
          <span class="selected-label">{t('forecast.selectedDay')}</span>
          <span class="selected-date">{store.selectedForecastDate}</span>
        </div>

        {#if loading}
          <div class="skeleton-rows">
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="50%" />
          </div>
        {:else if dayGroups.length === 0}
          <p class="hint">{t('forecast.noItems')}</p>
        {:else}
          <ul class="item-list">
            {#each dayGroups as g (g.key)}
              <li>
                <div class="item-card">
                  <div class="item-texts">
                    <span class="item-title">{primaryLabel(g)}</span>
                    {#if secondaryLabel(g)}
                      <span class="item-subtitle">{secondaryLabel(g)}</span>
                    {/if}
                  </div>
                  <Badge variant="accent" shape="pill" size="md">
                    {String(g.items.length)}
                  </Badge>
                </div>
              </li>
            {/each}
          </ul>

          <div class="selected-actions">
            <Button variant="filled" onclick={() => void reviewSelected()}>
              {t('forecast.reviewNow')}
            </Button>
          </div>
        {/if}
      {:else}
        <p class="hint">{t('forecast.selectHint')}</p>
      {/if}
    </div>

    {#if store.forecastStats.dueToday > 0}
      <div class="footer">
        <Button variant="outline" onclick={() => void reviewToday()}>
          {t('forecast.reviewAllDue')} · {store.forecastStats.dueToday}
        </Button>
      </div>
    {/if}
  {/if}
</section>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    width: 100%;
  }

  .view-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-3);
  }

  .view-title {
    margin: 0;
    font-size: var(--text-h1);
    font-weight: var(--text-h1-weight);
    letter-spacing: var(--text-h1-track);
    line-height: var(--text-h1-lh);
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .subtitle {
    margin: var(--space-1) 0 0;
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
  }

  .stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-2);
  }

  @media (max-width: 640px) {
    .stats {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .stat {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-3);
    background: var(--color-surface-sunken);
    border-radius: var(--radius-md);
  }

  .stat-label {
    font-size: var(--text-label);
    font-weight: var(--text-label-weight);
    letter-spacing: var(--text-label-track);
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .stat-value {
    font-family: var(--font-mono);
    font-size: var(--text-mono-data);
    font-weight: var(--weight-semibold);
    color: var(--color-text-primary);
    font-variant-numeric: tabular-nums;
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .heatmap {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: var(--space-2);
  }

  .cell {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: var(--space-3) 0;
    min-height: 64px;
    font-family: inherit;
    color: var(--color-text-primary);
    background: var(--heat-0);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition:
      border-color var(--dur-fast) var(--ease-out),
      transform var(--dur-instant) var(--ease-out);
  }

  .cell:active {
    transform: translateY(1px);
  }

  .cell[data-level='1'] {
    background: var(--heat-1);
    border-color: var(--heat-2);
  }
  .cell[data-level='2'] {
    background: var(--heat-2);
    border-color: var(--heat-3);
  }
  .cell[data-level='3'] {
    background: var(--heat-3);
    border-color: var(--heat-4);
    color: var(--color-accent-fg);
  }
  .cell[data-level='4'] {
    background: var(--heat-4);
    border-color: var(--heat-4);
    color: var(--color-accent-fg);
  }

  .cell[data-today] {
    border-color: var(--color-accent);
    border-width: 2px;
  }

  .cell[data-selected] {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 1px;
  }

  .cell:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  .cell-day {
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  .cell-month {
    font-size: 0.65rem;
    text-transform: uppercase;
    opacity: 0.75;
    line-height: 1;
  }

  .cell-count {
    position: absolute;
    top: 2px;
    right: 3px;
    font-size: 0.6rem;
    font-family: var(--font-mono);
    font-weight: var(--weight-bold);
    opacity: 0.9;
    font-variant-numeric: tabular-nums;
  }

  .selected {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    background: var(--color-surface-sunken);
    border-radius: var(--radius-md);
  }

  .selected-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--space-2);
  }

  .selected-label {
    font-size: var(--text-label);
    font-weight: var(--text-label-weight);
    letter-spacing: var(--text-label-track);
    text-transform: uppercase;
    color: var(--color-text-muted);
  }

  .selected-date {
    font-family: var(--font-mono);
    font-size: var(--text-mono-data);
    color: var(--color-text-primary);
    font-variant-numeric: tabular-nums;
  }

  .item-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .item-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
  }

  .item-texts {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .item-title {
    font-size: var(--text-body);
    font-weight: var(--weight-medium);
    color: var(--color-text-primary);
    overflow-wrap: anywhere;
  }

  .item-subtitle {
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    overflow-wrap: anywhere;
  }

  .selected-actions {
    display: flex;
    justify-content: flex-end;
  }

  .footer {
    display: flex;
    justify-content: flex-start;
  }

  .skeleton-rows {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
</style>